"use client";

import { useHistory, useLocation } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button-tw";
import { Checkbox } from "@/components/ui/check-box";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  AlertCircle,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Columns,
  Filter,
  ListFilter,
  Menu,
  Plus,
  Trash,
  XCircle,
  CircleEllipsis,
  Pen,
  Loader2,
  Upload,
  Banknote,
  Download,
  ArrowRight,
  Search,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "@/utils/classnames";
import { useInvoices } from "@/hooks/invoice/use-invoices";
import { addresseeNamePriority } from "@/utils/invoice";
import dayjs from "dayjs";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { useInvoiceDrawer } from "@/hooks/invoice/use-invoice-drawer";
import { useQuickPayViewDrawer } from "@/hooks/invoice/use-quickpay-view-drawer";
import { Calendar } from "@/components/ui/calendar";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { Slider } from "@/components/ui/slider";
import { hasPermission } from "@/utils/auth";
import { useAuth } from "@/providers/auth";
import { useCheckDepositDrawer } from "@/hooks/invoice/use-check-deposit-drawer";
import { exportToCsv } from "@/scripts/functions";
import { UploadInvoiceButton } from "./upload-invoice-button";
import { useDeleteInvoice } from "@/hooks/invoice/use-delete-invoice";
import { toast } from "react-toastify";
import { usePayableInvoices } from "@/hooks/invoice/use-payable-invoices";
import {
  PAST_DUE_INVOICE_STATUS,
  UNPAID_AND_OVERDUE_INVOICE_STATUS,
  UNPAID_INVOICE_STATUS,
} from "@/constants/invoice";

const formatLink = (link) => {
  try {
    new URL(link);
    return link;
  } catch (e) {
    return `https://${link}`;
  }
};

// Custom filter function for multi-column searching
const multiColumnFilterFn = (row, _columnId, filterValue) => {
  const searchableRowContent = `${
    row.original.invoice_info?.invoice_from?.name || ""
  } ${row.original.invoice_info?.invoice_from?.email}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const statusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const status = row.getValue(columnId);
  return filterValue.includes(status);
};

const amountFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const amount = row.getValue(columnId);
  return filterValue[0] <= amount && filterValue[1] >= amount;
};

const dateCreatedFilterFn = (row, columnId, filterValue) => {
  if (!filterValue) return true;

  const createdDate = dayjs(row.getValue(columnId));
  const fromDate = dayjs(filterValue.from).subtract(1, "day");
  const toDate = dayjs(filterValue.to).add(1, "day");
  return (
    createdDate.isAfter(fromDate, "day") && createdDate.isBefore(toDate, "day")
  );
};

const columns = [
  {
    id: "invoiceNumber",
    header: "Invoice #",
    accessorKey: "invoice_info.invoice_number",
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("invoiceNumber")}</div>
    ),
    size: 170,
  },
  {
    id: "name",
    header: "From",
    accessorFn: (row) => row.invoice_info?.invoice_from?.name,
    size: 220,
    filterFn: multiColumnFilterFn,
    enableHiding: false,
  },
  {
    header: "Email",
    accessorFn: (row) => row.invoice_info?.invoice_from?.email,
    // size: 220,
  },
  {
    header: "Date due",
    accessorFn: (row) => {
      return row.invoice_info.invoice_due_date
        ? dayjs(row.invoice_info.invoice_due_date).format("MM/DD/YYYY")
        : "";
    },
    size: 170,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.invoice_info?.invoice_due_date;
      const b = rowB.original.invoice_info?.invoice_due_date;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "invoice_info.invoice_status",
    cell: ({ row }) => <InvoiceStatusBadge status={row.getValue("status")} />,
    size: 170,
    enableSorting: false,
    filterFn: statusFilterFn,
  },
  {
    id: "amount",
    header: "Amount",
    accessorKey: "invoice_info.invoice_total",
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("amount"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
      return formatted;
    },
    size: 120,
    filterFn: amountFilterFn,
  },
  {
    id: "referenceNumber",
    header: "Reference #",
    accessorKey: "invoice_info.invoice_external_id",
    cell: ({ row }) =>
      row.original.invoice_info.invoice_external_id_link ? (
        <a
          href={formatLink(row.original.invoice_info.invoice_external_id_link)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700"
          style={{ textDecoration: "underline" }}
        >
          {row.getValue("referenceNumber") || "Click Here"}
        </a>
      ) : (
        row.getValue("referenceNumber") || ""
      ),
    size: 170,
    enableSorting: false,
  },
  {
    header: "Date created",
    accessorFn: (row) => {
      return row.created_at ? dayjs(row.created_at).format("MM/DD/YYYY") : "";
    },
    // size: 170,
    filterFn: dateCreatedFilterFn,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.created_at;
      const b = rowB.original.created_at;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    header: "Date sent",
    accessorFn: (row) => {
      return row.invoice_info.invoice_send_date
        ? dayjs(row.invoice_info.invoice_send_date).format("MM/DD/YYYY")
        : "";
    },
    // size: 170,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.invoice_info?.invoice_send_date;
      const b = rowB.original.invoice_info?.invoice_send_date;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    id: "@/actions/",
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <RowActions row={row} />
      </div>
    ),
    // size: 60,
    enableHiding: false,
  },
];
const fallbackData = [];

export default function PayablesTable() {
  const { data, isLoading } = usePayableInvoices();
  const { handleOpen: openInvoiceDrawer } = useInvoiceDrawer();
  const { handleOpen: openQuickPayViewDrawer } = useQuickPayViewDrawer();
  const history = useHistory();
  const id = useId();
  const tableRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    Email: false,
    "Date created": false,
    "Date sent": false,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const inputRef = useRef(null);

  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data: data || fallbackData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Get unique status values
  const uniqueStatusValues = useMemo(() => {
    const statusColumn = table.getColumn("status");

    if (!statusColumn) return [];

    const values = Array.from(statusColumn.getFacetedUniqueValues().keys());

    return values.sort();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  // Get counts for each status
  const statusCounts = useMemo(() => {
    const statusColumn = table.getColumn("status");
    if (!statusColumn) return new Map();
    return statusColumn.getFacetedUniqueValues();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  const selectedStatuses = useMemo(() => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    return filterValue ?? [];
  }, [table.getColumn("status")?.getFilterValue()]);

  const handleStatusChange = (checked, value) => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    const newFilterValue = filterValue ? [...filterValue] : [];

    if (checked) {
      newFilterValue.push(value);
    } else {
      const index = newFilterValue.indexOf(value);
      if (index > -1) {
        newFilterValue.splice(index, 1);
      }
    }

    table
      .getColumn("status")
      ?.setFilterValue(newFilterValue.length ? newFilterValue : undefined);
  };

  const minAmount = useMemo(() => {
    return data?.reduce(
      (acc, invoice) =>
        acc < invoice.invoice_info.invoice_total
          ? acc
          : invoice.invoice_info.invoice_total,
      Infinity
    );
  }, [data]);

  const maxAmount = useMemo(() => {
    return data?.reduce(
      (acc, invoice) =>
        acc > invoice.invoice_info.invoice_total
          ? acc
          : invoice.invoice_info.invoice_total,
      0
    );
  }, [data]);

  useEffect(() => {
    const filterValue = table.getColumn("amount")?.getFilterValue();
    console.log();
    if (!filterValue || filterValue[0] === undefined) {
      table.getColumn("amount")?.setFilterValue([minAmount, maxAmount]);
    }
  }, [data, minAmount, maxAmount]);

  const handleCreatedDateRangeChange = (range) => {
    table.getColumn("Date created")?.setFilterValue(range);
  };

  const formatAmount = (price) => {
    if (!price) return `$${Number("0").toLocaleString()}`;
    return price === maxAmount
      ? `$${price.toLocaleString()}+`
      : `$${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-4 mb-96">
      {/* Filters */}
      <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 flex-col-reverse lg:flex-row">
        <div className="flex items-center gap-3 grow flex-col lg:flex-row w-full">
          {/* Filter by name or email */}
          <div className="relative w-full lg:w-80">
            <Input
              id={`${id}-input`}
              ref={inputRef}
              className={cn(
                "peer min-w-60 ps-9",
                Boolean(table.getColumn("name")?.getFilterValue()) && "pe-9"
              )}
              value={table.getColumn("name")?.getFilterValue() ?? ""}
              onChange={(e) =>
                table.getColumn("name")?.setFilterValue(e.target.value)
              }
              placeholder="Search by name or email..."
              type="text"
              aria-label="Search by name or email"
            />
            <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
              <Search size={16} strokeWidth={2} aria-hidden="true" />
            </div>
            {Boolean(table.getColumn("name")?.getFilterValue()) && (
              <button
                className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Clear filter"
                onClick={() => {
                  table.getColumn("name")?.setFilterValue("");
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
              >
                <XCircle size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="grow flex justify-between w-full lg:w-auto">
            <div className="flex items-center gap-3 mr-3">
              {/* Filters */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="">
                    <Filter
                      className="-ms-1 me-2 opacity-60"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    Filters
                    {columnFilters.length > 0 && (
                      <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                        {columnFilters.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="min-w-[440px] p-3 py-0"
                  align="start"
                >
                  <div className="flex justify-between items-center my-2">
                    <div className="text-normal font-medium text-slate-800">
                      Filters
                    </div>
                    {/* <div className="text-xs font-medium text-muted-foreground"></div> */}
                    <Button
                      variant="link"
                      onClick={() =>
                        table.setColumnFilters([
                          { id: "amount", value: [minAmount, maxAmount] },
                        ])
                      }
                    >
                      Clear all
                    </Button>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value={"status"} className="py-2 border-t">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Status
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <div className="space-y-3">
                          {uniqueStatusValues.map((value, i) => (
                            <div
                              key={value}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`${id}-${i}`}
                                checked={selectedStatuses.includes(value)}
                                onCheckedChange={(checked) =>
                                  handleStatusChange(checked, value)
                                }
                              />
                              <Label
                                htmlFor={`${id}-${i}`}
                                className="flex grow justify-between gap-2 font-normal"
                              >
                                {value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}{" "}
                                <span className="ms-2 text-xs text-muted-foreground">
                                  {statusCounts.get(value)}
                                </span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value={"dateCreated"} className="py-2">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Date created
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <CreatedDateRangeFilter
                          date={table
                            .getColumn("Date created")
                            ?.getFilterValue()}
                          setDate={handleCreatedDateRangeChange}
                        />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value={"amount"} className="py-2">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Amount
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <div className="space-y-3">
                          <Label className="tabular-nums">
                            From{" "}
                            {formatAmount(
                              table.getColumn("amount")?.getFilterValue()?.[0]
                            )}{" "}
                            to{" "}
                            {formatAmount(
                              table.getColumn("amount")?.getFilterValue()?.[1]
                            )}
                          </Label>
                          <div className="flex items-center gap-4">
                            {minAmount !== undefined &&
                              maxAmount !== undefined && (
                                <Slider
                                  value={table
                                    .getColumn("amount")
                                    ?.getFilterValue()}
                                  onValueChange={(value) =>
                                    table
                                      .getColumn("amount")
                                      ?.setFilterValue(value)
                                  }
                                  min={minAmount}
                                  max={maxAmount}
                                  minStepsBetweenThumbs={20}
                                  aria-label="Amount range slider"
                                />
                              )}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </PopoverContent>
              </Popover>
              {/* Toggle columns visibility */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Columns
                      className="-ms-1 me-2 opacity-60"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    View
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                          onSelect={(event) => event.preventDefault()}
                        >
                          {column.id}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <TableActions table={table} />
          </div>
        </div>

        {/* Delete button */}
        {/* {table.getSelectedRowModel().rows.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="ml-auto" variant="outline">
                  <Trash
                    className="-ms-1 me-2 opacity-60"
                    size={16}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  Delete
                  <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                    {table.getSelectedRowModel().rows.length}
                  </span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <div className="flex flex-col gap-2 max-sm:items-center sm:flex-row sm:gap-4">
                  <div
                    className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border"
                    aria-hidden="true"
                  >
                    <AlertCircle
                      className="opacity-80"
                      size={16}
                      strokeWidth={2}
                    />
                  </div>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete{" "}
                      {table.getSelectedRowModel().rows.length} selected{" "}
                      {table.getSelectedRowModel().rows.length === 1
                        ? "row"
                        : "rows"}
                      .
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteRows}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )} */}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <Table className="" ref={tableRef}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-skin-primary bg-skin-primary"
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className="h-11"
                      //   colSpan={header.colSpan}
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <div
                          className={cn(
                            "group flex h-full cursor-pointer select-none items-center justify-between gap-2 text-white hover:text-white/80 break-keep",
                            {
                              "cursor-pointer": header.column.getCanSort(),
                            }
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            // Enhanced keyboard handling for sorting
                            if (
                              header.column.getCanSort() &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e);
                            }
                          }}
                          tabIndex={header.column.getCanSort() ? 0 : undefined}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <ChevronUp
                                className="shrink-0 "
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            ),
                            desc: (
                              <ChevronDown
                                className="shrink-0 "
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            ),
                          }[header.column.getIsSorted()] ?? null}
                          {
                            <ChevronUp
                              className={cn("shrink-0 hidden", {
                                "group-hover:inline-block":
                                  !header.column.getIsSorted(),
                              })}
                              size={16}
                              strokeWidth={2}
                              aria-hidden="true"
                            />
                          }
                        </div>
                      ) : (
                        <div className="text-white break-keep">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => {
                    if (row.original.invoice_info?.invoice_type === "quickpay") {
                      openQuickPayViewDrawer(row.original.invoice_id);
                    } else {
                      openInvoiceDrawer(row.original.invoice_id, true);
                    }
                  }}
                  className="hover:cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="last:py-0">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !table.getRowModel().rows?.length && isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 justify-items-center"
                >
                  <Loader2 className="w-9 h-9 animate-spin" />
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <DeleteInvoiceAlert />
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-8">
        {/* Results per page */}
        <div className="flex items-center gap-3">
          <Label htmlFor={id} className="max-sm:sr-only">
            Rows per page
          </Label>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger id={id} className="w-fit whitespace-nowrap">
              <SelectValue placeholder="Select number of results" />
            </SelectTrigger>
            <SelectContent className="[&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
              {[5, 10, 25, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize.toString()}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Page number information */}
        <div className="flex grow justify-end whitespace-nowrap text-sm text-muted-foreground">
          <p
            className="whitespace-nowrap text-sm text-muted-foreground"
            aria-live="polite"
          >
            <span className="text-foreground">
              {table.getState().pagination.pageIndex *
                table.getState().pagination.pageSize +
                1}
              -
              {Math.min(
                Math.max(
                  table.getState().pagination.pageIndex *
                    table.getState().pagination.pageSize +
                    table.getState().pagination.pageSize,
                  0
                ),
                table.getRowCount()
              )}
            </span>{" "}
            of{" "}
            <span className="text-foreground">
              {table.getRowCount().toString()}
            </span>
          </p>
        </div>

        {/* Pagination buttons */}
        <div>
          <Pagination>
            <PaginationContent>
              {/* First page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.firstPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Go to first page"
                >
                  <ChevronFirst size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Previous page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Go to previous page"
                >
                  <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Next page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Go to next page"
                >
                  <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
              {/* Last page button */}
              <PaginationItem>
                <Button
                  size="icon"
                  variant="outline"
                  className="disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => table.lastPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Go to last page"
                >
                  <ChevronLast size={16} strokeWidth={2} aria-hidden="true" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </div>
  );
}

function RowActions({ row }) {
  const { handleOpen: openInvoiceDrawer } = useInvoiceDrawer();
  const isQuickPay = row?.original?.invoice_info?.invoice_type === "quickpay";
  return (
    <div className="flex justify-end">
      {!isQuickPay &&
        [
          UNPAID_INVOICE_STATUS,
          PAST_DUE_INVOICE_STATUS,
          UNPAID_AND_OVERDUE_INVOICE_STATUS,
        ].includes(row?.original?.invoice_info?.invoice_status) && (
          <Button
            className="mr-2"
            onClick={() =>
              openInvoiceDrawer(row.original?.invoice_id, true, true)
            }
          >
            Pay
            <ArrowRight className="w-4 h-4 mf-2" />
          </Button>
        )}
    </div>
  );
}

function DeleteInvoiceAlert() {
  const history = useHistory();
  const location = useLocation();
  const { mutate: deleteInvoice, isLoading: deleting } = useDeleteInvoice();
  const queryParams = new URLSearchParams(location.search);

  const dialogOpen = queryParams.has("delete_invoice");
  const invoiceId = queryParams.get("delete_invoice");

  const closeDialog = () => {
    if (queryParams.has("delete_invoice")) {
      queryParams.delete("delete_invoice");
      history.replace({
        search: queryParams.toString(),
      });
    }
  };

  return (
    <AlertDialog open={dialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Double checking...</AlertDialogTitle>
          <AlertDialogDescription>
            <span
              className={cn(
                "text-sm text-muted-foreground text-center sm:text-left"
              )}
            >
              Are you sure you'd like to delete this invoice?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deleting}
            onClick={() => {
              closeDialog();
            }}
          >
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              deleteInvoice(invoiceId, {
                onSuccess: () => {
                  toast.success("Your invoice has been deleted.");
                  closeDialog();
                },
              });
            }}
            disabled={deleting}
          >
            Delete
            {deleting && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TableActions({ table }) {
  const { handleOpen: openMRDC } = useCheckDepositDrawer();
  const { user } = useAuth();

  const invoices = table
    .getPrePaginationRowModel()
    .flatRows.map((row) => row.original);

  const mapInvoiceForExport = (invoice) => {
    let res = {};
    if (!invoice) return res;
    const flatten = (source, prefix = "", exclude = []) => {
      if (!source) return;
      for (const [key, value] of Object.entries(source)) {
        if (!key.includes("id") && !exclude.includes(key)) {
          res[prefix ? `${prefix}_${key}` : key] = value;
        }
      }
    };

    flatten(invoice.invoice_info?.invoice_from, "invoice_from");
    flatten(invoice.invoice_info?.invoice_to, "invoice_to");
    flatten(invoice.invoice_info, "", [
      "invoice_from",
      "invoice_to",
      "invoice_line_items",
      "invoice_full_location",
    ]);
    flatten(invoice, "", ["invoice_info"]);

    return res;
  };

  const exportTable = (data = []) => {
    const content = data.map((item) => mapInvoiceForExport(item));
    exportToCsv(content || [], [], "Invoices");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          Actions
          <ChevronDown
            className="-me-1 ms-2 opacity-60"
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => exportTable(invoices)}>
          <div className="flex gap-x-2 ">
            <Download className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Export</span>
              <span className="text-xs text-muted-foreground">
                Export invoices to a csv file. The filters and sorting will be
                applied.
              </span>
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreatedDateRangeFilter({ date, setDate }) {
  const today = new Date();
  const yesterday = {
    from: subDays(today, 1),
    to: subDays(today, 1),
  };
  const last7Days = {
    from: subDays(today, 6),
    to: today,
  };
  const last30Days = {
    from: subDays(today, 29),
    to: today,
  };
  const monthToDate = {
    from: startOfMonth(today),
    to: today,
  };
  const lastMonth = {
    from: startOfMonth(subMonths(today, 1)),
    to: endOfMonth(subMonths(today, 1)),
  };
  const yearToDate = {
    from: startOfYear(today),
    to: today,
  };
  const lastYear = {
    from: startOfYear(subYears(today, 1)),
    to: endOfYear(subYears(today, 1)),
  };

  const [month, setMonth] = useState(date?.to);

  return (
    <div>
      <div className="rounded-lg border border-border">
        <div className="flex max-sm:flex-col">
          <div className="relative border-border py-4 max-sm:order-1 max-sm:border-t sm:w-36">
            <div className="h-full border-border sm:border-e">
              <div className="flex flex-col px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate({
                      from: today,
                      to: today,
                    });
                    setMonth(today);
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yesterday);
                    setMonth(yesterday.to);
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last7Days);
                    setMonth(last7Days.to);
                  }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last30Days);
                    setMonth(last30Days.to);
                  }}
                >
                  Last 30 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(monthToDate);
                    setMonth(monthToDate.to);
                  }}
                >
                  Month to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastMonth);
                    setMonth(lastMonth.to);
                  }}
                >
                  Last month
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yearToDate);
                    setMonth(yearToDate.to);
                  }}
                >
                  Year to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastYear);
                    setMonth(lastYear.to);
                  }}
                >
                  Last year
                </Button>
              </div>
            </div>
          </div>
          <Calendar
            mode="range"
            selected={date}
            onSelect={(newDate) => {
              if (newDate) {
                setDate(newDate);
              }
            }}
            month={month}
            onMonthChange={setMonth}
            className="p-2"
            disabled={[
              { after: today }, // Dates before today
            ]}
          />
        </div>
      </div>
    </div>
  );
}
